/**
 * Filter bar for the feedback list pages.
 *
 * Layout (desktop):
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ [🔍 Search…]  [Any time ▾]  [⚙ Filters (3) ▾]        Clear all │
 *   ╞──────────────────────────────────────────────────────────────────╡
 *   │ (Collapse — grid of multi-select Autocompletes)                  │
 *   ╞──────────────────────────────────────────────────────────────────╡
 *   │ Active: [Status: 2 ×] [Type: Bug ×] [Version: 0.9.5 ×]           │
 *   ╞──────────────────────────────────────────────────────────────────╡
 *   │ Showing 5 of 13 tickets                                          │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * All transitions are 180–240ms. Search is debounced (180ms) so the URL
 * doesn't thrash as the user types.
 */
import { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, Collapse, FormControl, IconButton,
  InputAdornment, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import {
  Close, ExpandLess, ExpandMore, FilterList, Search,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  bugSeverities, featurePriorities, feedbackStatuses,
} from '../../constants/statusOptions';
import type {
  FeedbackFilterState, FeedbackFilterUpdate, FilterOptions,
} from './useFeedbackFilters';

interface Props {
  state: FeedbackFilterState;
  update: (patch: FeedbackFilterUpdate) => void;
  clear: () => void;
  options: FilterOptions;
  activeCount: number;
  total: number;
  shown: number;
  isAdmin?: boolean;
}

const AGE_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'Any time' },
  { value: 1, label: 'Last 24 hours' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

export const FeedbackFilters = ({
  state, update, clear, options, activeCount, total, shown, isAdmin,
}: Props) => {
  const theme = useTheme();
  // Auto-expand the "more filters" panel on first render if any filters
  // are already active from the URL (so the user sees what's applied).
  const [expanded, setExpanded] = useState<boolean>(() => activeCount > 0);
  const [searchInput, setSearchInput] = useState(state.search);

  // Debounce search → URL, so every keystroke doesn't trigger a history
  // update. 180ms feels responsive without being chatty.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== state.search) update({ search: searchInput });
    }, 180);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  // If state changes from outside (e.g. Clear all), sync the input.
  useEffect(() => { setSearchInput(state.search); }, [state.search]);

  // Active-filter chips — one per applied filter, with individual remove.
  type ChipDef = { key: string; label: string; onRemove: () => void };
  const chips: ChipDef[] = [];
  const summarize = (vals: Set<string>, label: string, remove: () => void) => {
    if (vals.size === 0) return;
    const txt = vals.size === 1
      ? Array.from(vals)[0]
      : `${vals.size} selected`;
    chips.push({ key: label, label: `${label}: ${txt}`, onRemove: remove });
  };
  summarize(state.statuses, 'Status', () => update({ statuses: [] }));
  summarize(state.types, 'Type', () => update({ types: [] }));
  if (state.ageDays) {
    const age = AGE_OPTIONS.find((a) => a.value === state.ageDays);
    if (age) chips.push({ key: 'age', label: age.label, onRemove: () => update({ ageDays: null }) });
  }
  summarize(state.versions, 'Version', () => update({ versions: [] }));
  summarize(state.features, 'Feature', () => update({ features: [] }));
  summarize(state.severities, 'Severity', () => update({ severities: [] }));
  summarize(state.priorities, 'Priority', () => update({ priorities: [] }));
  summarize(state.submittedBy, 'Submitter', () => update({ submittedBy: [] }));

  const filterButtonSx = {
    textTransform: 'none' as const,
    fontSize: '0.82rem',
    whiteSpace: 'nowrap' as const,
    transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
    ...(activeCount > 0 && !expanded && {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      borderColor: alpha(theme.palette.primary.main, 0.4),
      color: 'primary.main',
    }),
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2, p: 1.5, borderRadius: 1.5,
        transition: 'border-color 200ms ease, box-shadow 200ms ease',
        ...(activeCount > 0 && {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: `0 1px 10px ${alpha(theme.palette.primary.main, 0.08)}`,
        }),
      }}
    >
      {/* Row 1: always visible — search, quick age, filters toggle, clear */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search ticket #, title, description…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} edge="end">
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: { fontSize: '0.85rem' },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={state.ageDays ?? ''}
            displayEmpty
            onChange={(e) => update({
              ageDays: e.target.value === '' ? null : Number(e.target.value),
            })}
            renderValue={(v) => {
              const opt = AGE_OPTIONS.find((o) => o.value === (v === '' ? null : Number(v)));
              return (
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                  {opt?.label ?? 'Any time'}
                </Typography>
              );
            }}
            sx={{ fontSize: '0.82rem' }}
          >
            {AGE_OPTIONS.map((o) => (
              <MenuItem key={String(o.value)} value={o.value ?? ''} sx={{ fontSize: '0.82rem' }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant={expanded ? 'contained' : 'outlined'}
          onClick={() => setExpanded(!expanded)}
          startIcon={<FilterList sx={{ fontSize: 18 }} />}
          endIcon={expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          sx={filterButtonSx}
          aria-expanded={expanded}
        >
          Filters
          {activeCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 0.75, px: 0.7, py: 0.05,
                borderRadius: 10,
                bgcolor: expanded ? 'rgba(255,255,255,0.25)' : 'primary.main',
                color: expanded ? '#fff' : '#fff',
                fontSize: '0.65rem', fontWeight: 700, minWidth: 18,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {activeCount}
            </Box>
          )}
        </Button>

        {activeCount > 0 && (
          <Button
            size="small"
            onClick={clear}
            sx={{
              textTransform: 'none', fontSize: '0.78rem',
              color: 'text.secondary',
              '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.04) },
            }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      {/* Row 2: expandable grid of multi-selects */}
      <Collapse in={expanded} timeout={240} mountOnEnter>
        <Box
          sx={{
            mt: 1.5, pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          }}
        >
          <MultiSelect
            label="Status"
            options={[...feedbackStatuses]}
            value={Array.from(state.statuses)}
            onChange={(v) => update({ statuses: v })}
          />
          <MultiSelect
            label="Type"
            options={['bug', 'feature']}
            value={Array.from(state.types)}
            onChange={(v) => update({ types: v })}
            display={(v) => (v === 'bug' ? 'Bug' : 'Feature Request')}
          />
          <MultiSelect
            label="App Version"
            options={options.versions}
            value={Array.from(state.versions)}
            onChange={(v) => update({ versions: v })}
            emptyOptionsHint="No versions in data yet"
          />
          <MultiSelect
            label="Feature / Area"
            options={options.features}
            value={Array.from(state.features)}
            onChange={(v) => update({ features: v })}
          />
          <MultiSelect
            label="Severity (bugs)"
            options={[...bugSeverities]}
            value={Array.from(state.severities)}
            onChange={(v) => update({ severities: v })}
          />
          <MultiSelect
            label="Priority (features)"
            options={[...featurePriorities]}
            value={Array.from(state.priorities)}
            onChange={(v) => update({ priorities: v })}
          />
          {isAdmin && (
            <MultiSelect
              label="Submitted By"
              options={options.submitters.map((s) => s.email)}
              value={Array.from(state.submittedBy)}
              onChange={(v) => update({ submittedBy: v })}
              display={(email) => {
                const e = options.submitters.find((s) => s.email === email);
                return e ? `${email} (${e.count})` : email;
              }}
              emptyOptionsHint="No submitters in data yet"
            />
          )}
        </Box>
      </Collapse>

      {/* Row 3: active filter chips */}
      <Collapse in={chips.length > 0} timeout={180} mountOnEnter>
        <Box sx={{ mt: 1.25, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 600 }}>
            Active:
          </Typography>
          {chips.map((c) => (
            <Chip
              key={c.key}
              size="small"
              variant="outlined"
              label={c.label}
              onDelete={c.onRemove}
              sx={{
                fontSize: '0.72rem', height: 22,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: 'text.primary',
                '& .MuiChip-deleteIcon': { fontSize: 14, color: 'text.secondary' },
                '& .MuiChip-deleteIcon:hover': { color: 'error.main' },
                transition: 'all 160ms ease',
              }}
            />
          ))}
        </Box>
      </Collapse>

      {/* Row 4: result count */}
      <Typography
        variant="caption"
        sx={{
          display: 'block', mt: 1,
          color: activeCount > 0 ? 'primary.main' : 'text.secondary',
          fontWeight: activeCount > 0 ? 600 : 400,
          transition: 'color 200ms ease',
        }}
      >
        {activeCount > 0
          ? `Showing ${shown} of ${total} ticket${total === 1 ? '' : 's'}`
          : `${total} ticket${total === 1 ? '' : 's'} total`}
      </Typography>
    </Paper>
  );
};

/** Multi-select via MUI Autocomplete — gives search-inside-dropdown for free. */
function MultiSelect({
  label, options, value, onChange, display, emptyOptionsHint,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  display?: (v: string) => string;
  emptyOptionsHint?: string;
}) {
  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={(v) => (display ? display(v) : v)}
      noOptionsText={emptyOptionsHint ?? 'No options'}
      disabled={options.length === 0}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={value.length === 0 ? (options.length === 0 ? emptyOptionsHint : 'Any') : ''}
          InputLabelProps={{ sx: { fontSize: '0.82rem' } }}
          sx={{ '& input': { fontSize: '0.82rem' } }}
        />
      )}
      renderTags={(vals, getTagProps) =>
        vals.map((v, idx) => {
          const { key, ...tagProps } = getTagProps({ index: idx });
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={display ? display(v) : v}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          );
        })
      }
      sx={{ '& .MuiAutocomplete-inputRoot': { fontSize: '0.82rem' } }}
    />
  );
}
